import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  User,
  ShieldCheck,
  Lock,
  Moon,
  Sun,
  Settings,
  Save,
  KeyRound,
  Clock4,
  MapPin,
  MonitorSmartphone,
  ShieldAlert,
  RefreshCw,
} from "lucide-react";

export default function AdminAccountPage() {
  const [darkMode, setDarkMode] = useState(true);
  const [themeColor, setThemeColor] = useState("#f2a900");
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  // 🧠 Fetch real login logs
  const fetchLogs = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get("https://api.speedfollowinfo.com/api/admin/login-logs");
      if (res.data.success) {
        setLoginLogs(res.data.logs);
      }
    } catch (err) {
      console.error("❌ Fetch Logs Error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ⏱️ Auto Refresh Every 30s
  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="min-h-screen p-8 transition-all duration-500"
      style={{
        background: darkMode
          ? "radial-gradient(circle at top left, #0b0f1a, #111827, #090c16)"
          : "radial-gradient(circle at top left, #f5f7ff, #e5e8f4)",
        color: darkMode ? "#fff" : "#000",
      }}
    >
      {/* 🧭 Header */}
      <div className="flex items-center justify-between mb-10">
        <h1
          className="text-3xl font-extrabold flex items-center gap-2 drop-shadow-[0_0_10px_rgba(255,215,0,0.3)]"
          style={{ color: themeColor }}
        >
          <Settings size={30} /> Admin Account Settings
        </h1>

        <button
          onClick={() => setDarkMode(!darkMode)}
          className="px-5 py-2 rounded-xl flex items-center gap-2 text-sm font-semibold border border-gray-500 hover:opacity-90 shadow-md transition-all"
          style={{
            background: darkMode
              ? "linear-gradient(135deg, #1e263f, #101425)"
              : "linear-gradient(135deg, #ffffff, #dfe3f3)",
          }}
        >
          {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          {darkMode ? "Light Mode" : "Dark Mode"}
        </button>
      </div>

      {/* 🔷 Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Left: Profile Info */}
        <div
          className="p-7 rounded-2xl shadow-xl border border-[#2f3b6c]/50 backdrop-blur-xl hover:scale-[1.01] transition-transform duration-300"
          style={{
            background: darkMode
              ? "linear-gradient(160deg, rgba(20,24,45,0.95), rgba(10,12,25,0.95))"
              : "linear-gradient(145deg, #ffffff, #e9eefc)",
          }}
        >
          <h2
            className="text-xl font-semibold mb-5 flex items-center gap-2"
            style={{ color: themeColor }}
          >
            <User size={22} /> Profile Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-gray-400 text-sm">Username</label>
              <input
                type="text"
                value={admin.username || "VIP Admin"}
                disabled
                className="w-full mt-1 p-3 rounded-lg bg-[#1b2142]/60 border border-[#2f3b6c]/60 text-gray-200"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Email Address</label>
              <input
                type="text"
                value={admin.email || "admin@speedfollow.com"}
                disabled
                className="w-full mt-1 p-3 rounded-lg bg-[#1b2142]/60 border border-[#2f3b6c]/60 text-gray-200"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Role</label>
              <input
                type="text"
                value={admin.role || "Super Admin"}
                disabled
                className="w-full mt-1 p-3 rounded-lg bg-[#1b2142]/60 border border-[#2f3b6c]/60 text-gray-200"
              />
            </div>
          </div>
        </div>

        {/* Right: Security Settings */}
        <div
          className="p-7 rounded-2xl shadow-xl border border-[#2f3b6c]/50 backdrop-blur-xl hover:scale-[1.01] transition-transform duration-300"
          style={{
            background: darkMode
              ? "linear-gradient(160deg, rgba(20,24,45,0.95), rgba(10,12,25,0.95))"
              : "linear-gradient(145deg, #ffffff, #e9eefc)",
          }}
        >
          <h2
            className="text-xl font-semibold mb-5 flex items-center gap-2"
            style={{ color: themeColor }}
          >
            <ShieldCheck size={22} /> Security Settings
          </h2>

          <div className="space-y-4">
            <input
              type="password"
              placeholder="Current Password"
              className="w-full p-3 rounded-lg bg-[#1b2142]/60 border border-[#2f3b6c]/60 text-gray-200"
            />
            <input
              type="password"
              placeholder="New Password"
              className="w-full p-3 rounded-lg bg-[#1b2142]/60 border border-[#2f3b6c]/60 text-gray-200"
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              className="w-full p-3 rounded-lg bg-[#1b2142]/60 border border-[#2f3b6c]/60 text-gray-200"
            />
            <button
              className="w-full py-3 rounded-lg font-bold text-black hover:opacity-95 transition-all shadow-md shadow-[#f2a900]/40"
              style={{
                background: `linear-gradient(90deg, ${themeColor}, #ff7b00)`,
              }}
            >
              <KeyRound className="inline-block mr-2" size={18} /> Update Password
            </button>
          </div>
        </div>
      </div>

      {/* 🎨 Theme & Appearance */}
      <div
        className="mt-10 p-7 rounded-2xl shadow-xl border border-[#2f3b6c]/50 backdrop-blur-xl"
        style={{
          background: darkMode
            ? "linear-gradient(160deg, rgba(20,24,45,0.95), rgba(10,12,25,0.95))"
            : "linear-gradient(145deg, #ffffff, #e9eefc)",
        }}
      >
        <h2
          className="text-xl font-semibold mb-5 flex items-center gap-2"
          style={{ color: themeColor }}
        >
          <Lock size={22} /> Theme & Appearance
        </h2>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">Primary Color</span>
            <input
              type="color"
              value={themeColor}
              onChange={(e) => setThemeColor(e.target.value)}
              className="w-10 h-10 rounded-full border-none outline-none cursor-pointer shadow-md shadow-[#000]/30"
            />
            <div
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: themeColor, color: "#000" }}
            >
              Preview
            </div>
          </div>

          <button
            className="px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-all shadow-md flex items-center gap-2"
            style={{
              background: `linear-gradient(90deg, ${themeColor}, #ff7b00)`,
              color: "#000",
            }}
          >
            <Save size={18} /> Save Theme
          </button>
        </div>
      </div>

      {/* 🕓 Login Activities (Bottom Section) */}
      <div
        className="mt-10 p-7 rounded-2xl shadow-xl border border-[#2f3b6c]/50 backdrop-blur-xl"
        style={{
          background: darkMode
            ? "linear-gradient(160deg, rgba(20,24,45,0.95), rgba(10,12,25,0.95))"
            : "linear-gradient(145deg, #ffffff, #e9eefc)",
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2
            className="text-xl font-semibold flex items-center gap-2"
            style={{ color: themeColor }}
          >
            <Clock4 size={22} /> Login Activities
          </h2>
          <button
            onClick={fetchLogs}
            className="flex items-center gap-2 text-sm text-gray-300 border border-gray-600 px-3 py-1 rounded-lg hover:bg-[#1e293b]/50 transition-all"
          >
            <RefreshCw
              size={15}
              className={refreshing ? "animate-spin text-[#1da1f2]" : "text-gray-300"}
            />
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-gray-400 text-sm text-center py-10">
            Loading recent logins...
          </p>
        ) : loginLogs.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-10">
            No login history found.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-y-2">
              <thead>
                <tr className="opacity-80 text-left">
                  <th className="p-2">Time</th>
                  <th className="p-2">IP</th>
                  <th className="p-2">Device</th>
                  <th className="p-2">Location</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loginLogs.map((log, index) => (
                  <tr
                    key={index}
                    className={`rounded-xl transition-all ${
                      darkMode
                        ? "bg-[#1b2142]/50 hover:bg-[#2a3264]/70"
                        : "bg-[#ffffff]/80 hover:bg-[#e9eefc]"
                    }`}
                  >
                    <td className="p-3 flex items-center gap-2">
                      <Clock4 size={14} className="opacity-70" />
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="p-3">{log.ipAddress}</td>
                    <td className="p-3 flex items-center gap-2">
                      <MonitorSmartphone size={15} className="opacity-70" />
                      {log.userAgent?.slice(0, 25) || "Unknown"}
                    </td>
                    <td className="p-3 flex items-center gap-2">
                      <MapPin size={15} className="opacity-70" />
                      {log.location || "Unknown"}
                    </td>
                    <td className="p-3">
                      {log.success ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <ShieldCheck size={14} /> Success
                        </span>
                      ) : (
                        <span className="text-red-400 flex items-center gap-1">
                          <ShieldAlert size={14} /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}